import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Megaphone, X } from "lucide-react";

const CONSENT_KEY = "cookie_consent_v1";
const GTM_ID = "GTM-KG5V5JGP";
const ADS_ID = "AW-18435552291";

const gtag = (...args) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

function loadGTM() {
  if (window.__gtmLoaded) return;
  window.__gtmLoaded = true;
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);
}

function loadAds() {
  if (window.__adsLoaded) return;
  window.__adsLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://www.googletagmanager.com/gtag/js?id=" + ADS_ID;
  document.head.appendChild(s);
  gtag("js", new Date());
  gtag("config", ADS_ID);
}

function applyConsent(c) {
  gtag("consent", "update", {
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    analytics_storage: c.analytics ? "granted" : "denied",
  });
  // GTM container (GTM-KG5V5JGP) ospita il tag Google Ads (marketing):
  // viene caricato solo con consenso marketing per non caricare alcun tracker
  // Google quando l'utente sceglie solo "Statistici".
  if (c.marketing) {
    loadGTM();
    loadAds();
  }
}

export const ConsentManager = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(CONSENT_KEY));
    } catch (e) {
      saved = null;
    }
    if (saved && typeof saved.analytics === "boolean") {
      applyConsent(saved);
      setAnalytics(saved.analytics);
      setMarketing(saved.marketing);
    } else {
      setVisible(true);
    }
  }, []);

  const openPrefs = useCallback(() => {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(CONSENT_KEY));
    } catch (e) {
      saved = null;
    }
    setAnalytics(saved?.analytics || false);
    setMarketing(saved?.marketing || false);
    setShowPrefs(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener("open-cookie-preferences", openPrefs);
    return () => window.removeEventListener("open-cookie-preferences", openPrefs);
  }, [openPrefs]);

  const save = (c) => {
    const consent = { necessary: true, analytics: !!c.analytics, marketing: !!c.marketing, ts: Date.now() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    applyConsent(consent);
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-consent" className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 pointer-events-none">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm pointer-events-auto" onClick={() => {}} />
      <div
        data-testid="cookie-banner"
        className="relative pointer-events-auto w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-slate-100 p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start gap-4">
          <span className="grid place-items-center h-12 w-12 shrink-0 rounded-2xl bg-rose-100 text-rose-500">
            <Cookie className="h-6 w-6" />
          </span>
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-700 text-slate-800">Rispettiamo la tua privacy 🍪</h2>
            <p className="mt-2 text-slate-600 font-600 text-sm sm:text-base leading-relaxed">
              Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, cookie
              statistici e di marketing. Puoi accettare, rifiutare o personalizzare le tue scelte. Per maggiori
              dettagli consulta la{" "}
              <Link to="/cookie-policy" data-testid="banner-cookie-policy-link" className="text-rose-500 font-800 underline" onClick={() => setVisible(false)}>Cookie Policy</Link>{" "}
              e la{" "}
              <Link to="/privacy-policy" data-testid="banner-privacy-policy-link" className="text-rose-500 font-800 underline" onClick={() => setVisible(false)}>Privacy Policy</Link>.
            </p>
          </div>
        </div>

        {showPrefs && (
          <div className="mt-6 space-y-3" data-testid="cookie-preferences-panel">
            <PrefRow icon={Shield} color="text-emerald-600 bg-emerald-100" title="Necessari" desc="Indispensabili per il funzionamento del sito. Sempre attivi." locked checked />
            <PrefRow icon={BarChart3} color="text-sky-600 bg-sky-100" title="Statistici" desc="Ci aiutano a capire come viene usato il sito (es. Google Analytics)." checked={analytics} onChange={setAnalytics} testid="toggle-analytics" />
            <PrefRow icon={Megaphone} color="text-amber-600 bg-amber-100" title="Marketing" desc="Usati per annunci personalizzati (es. Google Ads, Tag Manager)." checked={marketing} onChange={setMarketing} testid="toggle-marketing" />
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          {!showPrefs ? (
            <>
              <button data-testid="cookie-accept-all" onClick={() => save({ analytics: true, marketing: true })}
                className="order-1 sm:order-3 flex-1 rounded-full bg-rose-500 text-white px-6 py-3.5 font-800 hover:bg-rose-600 transition-colors">
                Accetta tutti
              </button>
              <button data-testid="cookie-reject-all" onClick={() => save({ analytics: false, marketing: false })}
                className="order-2 flex-1 rounded-full bg-slate-100 text-slate-700 px-6 py-3.5 font-800 hover:bg-slate-200 transition-colors">
                Rifiuta
              </button>
              <button data-testid="cookie-customize" onClick={() => setShowPrefs(true)}
                className="order-3 sm:order-1 flex-1 rounded-full bg-white text-slate-700 border border-slate-200 px-6 py-3.5 font-800 hover:border-rose-300 transition-colors">
                Personalizza
              </button>
            </>
          ) : (
            <>
              <button data-testid="cookie-save-prefs" onClick={() => save({ analytics, marketing })}
                className="order-2 sm:order-1 flex-1 rounded-full bg-slate-800 text-white px-6 py-3.5 font-800 hover:bg-slate-900 transition-colors">
                Salva preferenze
              </button>
              <button data-testid="cookie-accept-all-prefs" onClick={() => save({ analytics: true, marketing: true })}
                className="order-1 sm:order-2 flex-1 rounded-full bg-rose-500 text-white px-6 py-3.5 font-800 hover:bg-rose-600 transition-colors">
                Accetta tutti
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PrefRow = ({ icon: Icon, color, title, desc, checked, onChange, locked, testid }) => (
  <div className="flex items-start gap-3 rounded-2xl bg-[#f7f0e3] p-4">
    <span className={`grid place-items-center h-10 w-10 shrink-0 rounded-xl ${color}`}>
      <Icon className="h-5 w-5" />
    </span>
    <div className="flex-1">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading font-700 text-slate-800">{title}</h3>
        {locked ? (
          <span className="text-xs font-800 uppercase tracking-wider text-emerald-600">Sempre attivi</span>
        ) : (
          <button
            type="button"
            data-testid={testid}
            onClick={() => onChange(!checked)}
            className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-rose-500" : "bg-slate-300"}`}
            aria-pressed={checked}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-600 font-600">{desc}</p>
    </div>
  </div>
);
